import ConfirmationModal from '@/components/ui-kit/confirmation-modal';
import { useToast } from '@/hooks/use-toast';

import { useDeleteKbById, useGetAllKB } from '@/modules/gpt-chats/hooks/use-kb';
import { KBType } from '@/modules/gpt-chats/models/kb';
import { Dialog } from '@radix-ui/react-dialog';
import { ErrorTransformer } from '@/modules/gpt-chats/utils/error-transform';
import { useDeleteFile } from '@/lib/api/hooks/use-storage-file';
import { useTranslation } from 'react-i18next';

type DeleteKbProps = {
  folderId: string;
  kbId: string;
  kbType: KBType;
  open: boolean;
  onOpenChange: (value: boolean) => void;
};

export const DeleteKb = ({ folderId, kbId, kbType, open, onOpenChange }: DeleteKbProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { mutateAsync, isPending } = useDeleteKbById(kbType);

  const { mutateAsync: deleteFile, isPending: isDeletingFile } = useDeleteFile();

  const { isLoading: isFileDataLoading, data: fileData } = useGetAllKB(
    {
      kb_type: KBType.FILE,
      limit: 1,
      offset: 0,
      folder_id: folderId,
      ids: [kbId],
    },
    !!kbId && !!folderId && kbType === KBType.FILE
  );

  const confirmHandler = async () => {
    try {
      const res = await mutateAsync({
        kb_id: kbId,
      });

      if (!res.is_success) return toast.error({ errors: res.error });

      if (kbType === KBType.FILE && fileData?.data?.knowledge_bases?.[0]?.file?.file_id) {
        await deleteFile({
          fileId: fileData.data?.knowledge_bases[0].file.file_id,
        });
      }

      toast.success(t('CONTENT_DELETED_SUCCESSFULLY'));
    } catch (error) {
      return toast.error({ errors: ErrorTransformer(error) });
    } finally {
      onOpenChange(false);
    }
  };

  const isDataNeededButLoading = kbType === KBType.FILE && isFileDataLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ConfirmationModal
        data={{
          dialogTitle: t('DELETE_CONFIRM_TITLE'),
          dialogSubtitle: t('DELETE_CONFIRM_SUBTITLE'),
        }}
        onConfirm={confirmHandler}
        onCancel={() => onOpenChange(false)}
        buttonState={{
          confirm: { disable: isPending || isDeletingFile || isDataNeededButLoading },
        }}
      />
    </Dialog>
  );
};
